<template>
  <li v-if="showButton" class="header-notification-nav" ref="pendingDownloadsNav">
    <button
      class="header-action-btn pending-downloads-btn"
      type="button"
      @click.stop="openModal"
      :title="buttonTitle"
    >
      <i :class="buttonIconClass"></i>
      <span class="pending-attention-dot" :class="{ 'is-progress': hasInProgress }" aria-hidden="true"></span>
    </button>
  </li>

  <teleport to="body">
    <div v-if="modalVisible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" @click.self="closeModal">
      <div class="modal-dialog modal-dialog-centered pending-request-modal-dialog" role="document">
        <div class="modal-content pending-request-modal-content">
          <div class="modal-header pending-request-modal-header">
            <h5 class="modal-title"><i class="fa fa-download text-warning me-2"></i>Descargas en curso</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
          </div>
          <div class="modal-body pending-request-modal-body">
            <p class="pending-request-title mb-1">{{ modalData.title }}</p>
            <p v-if="modalData.subtitle" class="pending-request-subtitle mb-3">{{ modalData.subtitle }}</p>

            <div class="pending-request-info-row">
              <div class="pending-request-info-item">
                <span class="pending-request-info-label">RFC</span>
                <span class="pending-request-info-value">{{ normalizedRfc || '-' }}</span>
              </div>
              <div class="pending-request-info-item">
                <span class="pending-request-info-label">Estado</span>
                <span class="pending-request-info-value">{{ modalData.statusLabel || '-' }}</span>
              </div>
              <div class="pending-request-info-item">
                <span class="pending-request-info-label">Tipo</span>
                <span class="pending-request-info-value">{{ modalData.typeLabel || '-' }}</span>
              </div>
              <div class="pending-request-info-item">
                <span class="pending-request-info-label">Periodo</span>
                <span class="pending-request-info-value">{{ modalData.periodLabel || '-' }}</span>
              </div>
            </div>

            <div class="pending-request-warning">
              <i class="fa fa-info-circle"></i>
              <div>
                <strong>{{ modalData.statusLabel || 'Descarga en proceso' }}</strong>
                <p>{{ modalData.scheduleLabel || 'Mantén esta ventana abierta para seguir el avance en la tabla del Centro de Descargas.' }}</p>
              </div>
            </div>
          </div>
          <div class="modal-footer pending-request-modal-footer">
            <button type="button" class="btn btn-light" @click="goToDownloadCenter">Ir al centro de descargas</button>
            <button type="button" class="btn btn-primary" @click="closeModal">Entendido</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="modalVisible" class="modal-backdrop fade show"></div>
  </teleport>
</template>

<script>
import dashboardService from '@/services/dashboard';

const POLL_INTERVAL_MS = 60000;

export default {
  name: 'PendingDownloadsHeaderAction',
  data() {
    return {
      hasInProgress: false,
      hasRequested: false,
      pendingHint: null,
      modalVisible: false,
      modalData: {
        title: '',
        subtitle: '',
        statusLabel: '',
        typeLabel: '',
        periodLabel: '',
        scheduleLabel: '',
      },
      pollingTimerId: null,
      loadingState: false,
    };
  },
  computed: {
    normalizedRfc() {
      return String(localStorage.getItem('rfc') || '').trim().toUpperCase();
    },
    showButton() {
      return this.hasInProgress || this.hasRequested;
    },
    buttonIconClass() {
      if (this.hasInProgress) {
        return 'fa fa-refresh fa-spin';
      }

      return 'fa fa-download';
    },
    buttonTitle() {
      if (this.hasInProgress) {
        return 'Descargando';
      }

      return 'Por Descargar';
    },
  },
  methods: {
    emitHeaderDropdownOpen(source) {
      window.dispatchEvent(new CustomEvent('header-dropdown-open', { detail: { source } }));
    },
    handleHeaderDropdownOpen(event) {
      const source = event && event.detail ? event.detail.source : '';
      if (source !== 'pending-downloads') {
        this.modalVisible = false;
      }
    },
    normalizeStatus(status) {
      const normalized = String(status || '').trim().toLowerCase();

      if (normalized === 'downloading' || normalized === 'processing') {
        return 'in_progress';
      }

      if (normalized === 'pending') {
        return 'requested';
      }

      return normalized;
    },
    parseDateTimeValue(value) {
      const raw = String(value || '').trim();
      if (!raw) {
        return null;
      }

      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const parsedDate = new Date(normalized);
      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return parsedDate;
    },
    formatDateTimeLabel(value) {
      const parsedDate = this.parseDateTimeValue(value);
      if (!parsedDate) {
        return String(value || '').trim();
      }

      return parsedDate.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    },
    formatPeriodLabel(period) {
      const normalized = String(period || '').trim();
      if (!normalized) {
        return 'Periodo no definido';
      }

      const monthMatch = normalized.match(/^(\d{4})-(\d{2})$/);
      if (monthMatch) {
        const year = Number(monthMatch[1]);
        const month = Number(monthMatch[2]);
        if (month >= 1 && month <= 12) {
          const formatted = new Intl.DateTimeFormat('es-MX', {
            month: 'long',
            year: 'numeric',
          }).format(new Date(year, month - 1, 1));

          return formatted.charAt(0).toUpperCase() + formatted.slice(1);
        }
      }

      const yearMatch = normalized.match(/^(\d{4})$/);
      if (yearMatch) {
        return `Año ${yearMatch[1]}`;
      }

      return normalized;
    },
    parseDateFromDdMmYyyy(value) {
      const raw = String(value || '').trim();
      const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!match) {
        return null;
      }

      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);
      if (!day || !month || !year) {
        return null;
      }

      return new Date(year, month - 1, day);
    },
    formatMonthYearFromDate(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return 'Periodo no definido';
      }

      const formatted = new Intl.DateTimeFormat('es-MX', {
        month: 'long',
        year: 'numeric',
      }).format(date);

      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },
    buildPendingHintFromGroups(groups) {
      const candidateGroups = [];

      if (Array.isArray(groups?.manual_today)) {
        candidateGroups.push(...groups.manual_today);
      }

      if (Array.isArray(groups?.daily_last_10_days)) {
        candidateGroups.push(...groups.daily_last_10_days);
      }

      if (groups?.initial_summary) {
        candidateGroups.push(groups.initial_summary);
      }

      if (!candidateGroups.length) {
        return null;
      }

      const statusPriority = {
        in_progress: 0,
        downloading: 0,
        requested: 1,
      };

      const pending = candidateGroups
        .map((item) => {
          const status = String(item?.status || '').trim().toLowerCase();
          if (!(status in statusPriority)) {
            return null;
          }

          return {
            raw: item,
            status,
            priority: statusPriority[status],
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority;
          }

          const aDate = String(a.raw?.requested_at_raw || '');
          const bDate = String(b.raw?.requested_at_raw || '');
          if (aDate === bDate) {
            return 0;
          }

          return aDate < bDate ? -1 : 1;
        });

      if (!pending.length) {
        return null;
      }

      const selected = pending[0].raw || {};
      const types = Array.isArray(selected?.types) ? selected.types.map((t) => String(t || '').toLowerCase()) : [];

      let natureLabel = 'Recibidos';
      if (types.includes('issued') && types.includes('received')) {
        natureLabel = 'Recibidos y Emitidos';
      } else if (types.includes('issued')) {
        natureLabel = 'Emitidos';
      }

      const rangeEndDate = this.parseDateFromDdMmYyyy(selected?.range_end);
      const rangeStartDate = this.parseDateFromDdMmYyyy(selected?.range_start);
      const periodDate = rangeEndDate || rangeStartDate || null;
      const periodLabel = this.formatMonthYearFromDate(periodDate);

      return {
        status: pending[0].status,
        nature_label: natureLabel,
        period_label: periodLabel,
        scheduled_date: selected?.scheduled_at_raw || selected?.requested_at_raw || '',
      };
    },
    buildPendingHintFromCfdiDownloads(downloads) {
      if (!Array.isArray(downloads) || !downloads.length) {
        return null;
      }

      const statusPriority = {
        in_progress: 0,
        requested: 1,
      };

      const pendingRows = downloads
        .map((item) => {
          const status = this.normalizeStatus(item?.status);
          if (!(status in statusPriority)) {
            return null;
          }

          return {
            raw: item,
            status,
            priority: statusPriority[status],
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority;
          }

          const aDate = String(a.raw?.requested_at_raw || a.raw?.requested_at || '');
          const bDate = String(b.raw?.requested_at_raw || b.raw?.requested_at || '');
          if (aDate === bDate) {
            return 0;
          }

          return aDate < bDate ? -1 : 1;
        });

      if (!pendingRows.length) {
        return null;
      }

      const selected = pendingRows[0];
      return {
        status: selected.status,
        type: String(selected.raw?.nature || '').trim().toLowerCase(),
        period: selected.raw?.period || '',
        scheduled_date: selected.raw?.scheduled_at_raw || selected.raw?.requested_at_raw || selected.raw?.requested_at || '',
      };
    },
    buildModalData(item) {
      const status = this.normalizeStatus(item?.status);
      const explicitNatureLabel = String(item?.nature_label || '').trim();
      const type = String(item?.type || '').trim().toLowerCase();
      const natureLabel = explicitNatureLabel || (type === 'issued' ? 'Emitidos' : 'Recibidos');
      const explicitPeriodLabel = String(item?.period_label || '').trim();
      const periodLabel = explicitPeriodLabel || this.formatPeriodLabel(item?.period || '');
      const effectiveDateRaw = item?.scheduled_date || item?.request_date || '';
      const effectiveDate = this.parseDateTimeValue(effectiveDateRaw);
      const dateLabel = this.formatDateTimeLabel(effectiveDateRaw);
      const isScheduledDateOverdue = Boolean(effectiveDate && effectiveDate.getTime() < Date.now());

      if (status === 'in_progress') {
        return {
          title: 'Descargando CFDIs',
          subtitle: `CFDIs ${natureLabel} - ${periodLabel}`,
          statusLabel: 'En proceso',
          typeLabel: natureLabel,
          periodLabel,
          scheduleLabel: 'La descarga está actualmente en ejecución.',
        };
      }

      return {
        title: 'Preparando descarga de CFDIs',
        subtitle: `CFDIs ${natureLabel} - ${periodLabel}`,
        statusLabel: 'Esperando',
        typeLabel: natureLabel,
        periodLabel,
        scheduleLabel: isScheduledDateOverdue
          ? 'La solicitud sigue esperando. Hay muchas peticiones de descarga o existe intermitencia en el SAT.'
          : dateLabel
            ? `Programada para: ${dateLabel}`
            : 'La solicitud está pendiente y se ejecutará en cuanto haya disponibilidad.',
      };
    },
    async refreshPendingState() {
      if (this.loadingState) {
        return;
      }

      if (!this.normalizedRfc) {
        this.hasInProgress = false;
        this.hasRequested = false;
        this.pendingHint = null;
        this.modalVisible = false;
        return;
      }

      this.loadingState = true;
      try {
        const response = await dashboardService.getDownloadCenterHistory(this.normalizedRfc, 1, 200, { skipLoading: true });
        const cfdiDownloads = Array.isArray(response?.cfdi_file_downloads) ? response.cfdi_file_downloads : [];

        let hasInProgress = false;
        let hasRequested = false;

        cfdiDownloads.forEach((row) => {
          const status = this.normalizeStatus(row?.status);
          if (status === 'in_progress') {
            hasInProgress = true;
          }
          if (status === 'requested') {
            hasRequested = true;
          }
        });

        const selectedHint = response?.pending_request_hint
          || this.buildPendingHintFromCfdiDownloads(cfdiDownloads)
          || this.buildPendingHintFromGroups(response?.groups)
          || null;

        if (selectedHint) {
          const hintStatus = this.normalizeStatus(selectedHint?.status);
          hasInProgress = hasInProgress || hintStatus === 'in_progress';
          hasRequested = hasRequested || hintStatus === 'requested';
        }

        this.hasInProgress = hasInProgress;
        this.hasRequested = hasRequested;
        this.pendingHint = selectedHint;

        if (!this.showButton) {
          this.modalVisible = false;
          return;
        }

        if (this.modalVisible && this.pendingHint) {
          this.modalData = this.buildModalData(this.pendingHint);
        }
      } catch (_) {
        this.hasInProgress = false;
        this.hasRequested = false;
        this.pendingHint = null;
        this.modalVisible = false;
      } finally {
        this.loadingState = false;
      }
    },
    async openModal() {
      this.modalVisible = true;
      this.emitHeaderDropdownOpen('pending-downloads');
      await this.refreshPendingState();

      if (!this.pendingHint) {
        this.modalVisible = false;
        return;
      }

      this.modalData = this.buildModalData(this.pendingHint);
    },
    closeModal() {
      this.modalVisible = false;
    },
    goToDownloadCenter() {
      this.modalVisible = false;
      if (this.$route?.path === '/centro-descargas') {
        return;
      }

      this.$router.push('/centro-descargas').catch(() => {});
    },
    handleRfcsStorageUpdated() {
      this.refreshPendingState();
    },
    handleStorageEvent(event) {
      if (!event || event.key === null || event.key === 'rfc' || event.key === 'rfcs') {
        this.refreshPendingState();
      }
    },
    handleEscapeClose(event) {
      if (event?.key === 'Escape' && this.modalVisible) {
        this.closeModal();
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollingTimerId = setInterval(() => {
        this.refreshPendingState();
      }, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollingTimerId) {
        clearInterval(this.pollingTimerId);
        this.pollingTimerId = null;
      }
    },
  },
  mounted() {
    window.addEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.addEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.addEventListener('storage', this.handleStorageEvent);
    window.addEventListener('keydown', this.handleEscapeClose);
    this.refreshPendingState();
    this.startPolling();
  },
  beforeUnmount() {
    window.removeEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.removeEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.removeEventListener('storage', this.handleStorageEvent);
    window.removeEventListener('keydown', this.handleEscapeClose);
    this.stopPolling();
  },
  beforeDestroy() {
    window.removeEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.removeEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.removeEventListener('storage', this.handleStorageEvent);
    window.removeEventListener('keydown', this.handleEscapeClose);
    this.stopPolling();
  },
};
</script>

<style scoped>
.header-notification-nav {
  position: relative;
  display: flex;
  align-items: center;
}

.header-action-btn {
  border: 0;
  background: var(--white);
  height: 52px;
  min-height: 52px;
  min-width: 46px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid var(--sidebar-border);
  transition: all 0.15s ease;
  position: relative;
}

.header-action-btn i {
  font-size: 16px;
  color: var(--theme-font-color);
  opacity: 0.8;
}

.header-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px var(--sidebar-border);
}

.header-action-btn:hover i {
  color: var(--theme-deafult);
  opacity: 1;
}

.pending-downloads-btn {
  overflow: visible;
}

.pending-attention-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #f29f05;
  box-shadow: 0 0 0 0 rgba(242, 159, 5, 0.55);
  animation: pendingPulsePending 1.6s infinite;
}

.pending-attention-dot.is-progress {
  background: #1e8c59;
  box-shadow: 0 0 0 0 rgba(30, 140, 89, 0.5);
  animation: pendingPulseProgress 1.6s infinite;
}

@keyframes pendingPulsePending {
  0% {
    box-shadow: 0 0 0 0 rgba(242, 159, 5, 0.55);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(242, 159, 5, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(242, 159, 5, 0);
  }
}

@keyframes pendingPulseProgress {
  0% {
    box-shadow: 0 0 0 0 rgba(30, 140, 89, 0.5);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(30, 140, 89, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(30, 140, 89, 0);
  }
}

.pending-request-modal-dialog {
  max-width: 560px;
}

.pending-request-modal-content {
  border-radius: 14px;
  overflow: hidden;
}

.pending-request-modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 16px 20px;
}

.pending-request-modal-body {
  padding: 18px 20px 14px;
}

.pending-request-title {
  font-size: 20px;
  font-weight: 700;
  color: #2f2f4a;
}

.pending-request-subtitle {
  font-size: 14px;
  color: #6c757d;
}

.pending-request-info-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  background: #f4f6fb;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
}

.pending-request-info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pending-request-info-label {
  font-size: 11px;
  font-weight: 700;
  color: #8a8da2;
  text-transform: uppercase;
  letter-spacing: .4px;
}

.pending-request-info-value {
  font-size: 15px;
  font-weight: 700;
  color: #2f3b59;
}

.pending-request-warning {
  display: flex;
  gap: 10px;
  border: 1px solid #ffd266;
  background: #fff8e5;
  border-radius: 10px;
  padding: 12px 14px;
  color: #7a5a00;
}

.pending-request-warning i {
  font-size: 18px;
  margin-top: 2px;
}

.pending-request-warning strong {
  display: block;
  font-size: 14px;
  margin-bottom: 2px;
}

.pending-request-warning p {
  margin: 0;
  font-size: 13px;
}

.pending-request-modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 12px 20px 16px;
}

@media (max-width: 576px) {
  .pending-request-info-row {
    grid-template-columns: 1fr;
  }
}
</style>